import React, { memo } from "react";

import { ShowcaseCard, ShowcaseIntro, ShowcaseNotice } from "ui/components/Showcase";

import { ImportIntroProps } from "./types";

const TEMPLATE_COLUMNS = ["JOM", "VIN", "Registracija", "Km"];

/** Naslov i opis ispod scene, po koraku. */
const IMPORT_INTRO_TEXTS = [
  {
    title: "Preuzmite predložak",
    description: "Predložak ima sve stupce koje uvoz traži. Upišite vozila redak po redak i spremite datoteku.",
  },
  {
    title: "Učitajte ispunjenu datoteku",
    description: "Sustav provjerava svaki redak. Ispravna vozila se uvoze, a neispravni reci se preskaču uz poruku.",
  },
];

/** Lijevi panel uvoza vozila iz Excela: predložak sa stupcima, zatim provjera učitane datoteke. */
const ImportIntro: React.FC<ImportIntroProps> = ({ stepCurrent, stepCount, fileName }) => {
  const isFileStep = stepCurrent === 1;
  const text = IMPORT_INTRO_TEXTS[isFileStep ? 1 : 0];

  return (
    <ShowcaseIntro
      sceneKey={stepCurrent}
      title={text.title}
      description={text.description}
      stepCurrent={stepCurrent}
      stepCount={stepCount}
    >
      {!isFileStep ? (
        <>
          <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
            <div className="p-5">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-7 h-7 rounded-sm bg-emerald-600 text-[0.6rem] font-bold text-white">
                  XLS
                </span>
                <div className="text-sm font-bold text-slate-800 truncate">FLEET_VEHICLE.xlsx</div>
              </div>

              {/* Prazan predložak: zaglavlje sa stupcima i prazni redovi za upis. */}
              <div className="mt-3 rounded-sm border border-slate-200 overflow-hidden text-[0.6rem]">
                <div className="grid grid-cols-4 bg-emerald-50 font-semibold text-emerald-800">
                  {TEMPLATE_COLUMNS.map((column) => (
                    <span key={column} className="px-2 py-1.5 border-r border-slate-200 last:border-r-0 truncate">
                      {column}
                    </span>
                  ))}
                </div>
                {[0, 1, 2, 3, 4].map((row) => (
                  <div key={row} className="grid grid-cols-4 border-t border-slate-200">
                    {TEMPLATE_COLUMNS.map((column) => (
                      <span key={column} className="h-6 border-r border-slate-200 last:border-r-0" />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </ShowcaseCard>

          <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
            <div className="p-5">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Obavezno</div>
              <div className="mt-2 text-xs font-bold text-slate-800">JOM broj vozila</div>
              <div className="mt-1 text-[0.65rem] text-slate-400">Ostali podaci stižu iz kataloga</div>
            </div>
          </ShowcaseCard>

          <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
            <ShowcaseNotice
              title="Predložak je spreman"
              subtitle="Stupci su već složeni"
              iconClassName="bg-emerald-100"
              icon={
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                </svg>
              }
            />
          </ShowcaseCard>
        </>
      ) : (
        <>
          <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
            <div className="p-5">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-7 h-7 rounded-sm bg-emerald-600 text-[0.6rem] font-bold text-white">
                  XLS
                </span>
                <div className="text-sm font-bold text-slate-800 truncate">{fileName || "vozila.xlsx"}</div>
              </div>

              {/* Provjera redaka: ispravni prolaze, neispravni se preskaču. */}
              <div className="mt-4 space-y-2">
                {["Redak 2", "Redak 3", "Redak 4", "Redak 5"].map((row, index) => {
                  const isSkipped = index === 2;
                  return (
                    <div key={row} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-[0.7rem]">
                      <span className="text-slate-600">{row}</span>
                      <span className={isSkipped ? "font-semibold text-amber-600" : "font-semibold text-emerald-600"}>
                        {isSkipped ? "Preskočen" : "Uvezen"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </ShowcaseCard>

          <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
            <div className="p-5">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Rezultat uvoza</div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-xl font-bold text-slate-800">24</span>
                <span className="text-[0.65rem] text-slate-400">uvezeno</span>
              </div>
              <div className="text-[0.65rem] font-semibold text-amber-600">1 preskočeno</div>
            </div>
          </ShowcaseCard>

          <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
            <ShowcaseNotice
              title="Neispravni reci se preskaču"
              subtitle="Ostala vozila se uvoze"
              iconClassName="bg-amber-100"
              icon={
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
                </svg>
              }
            />
          </ShowcaseCard>
        </>
      )}
    </ShowcaseIntro>
  );
};

export default memo(ImportIntro);
